/**
 * NarrativeEscalationMapping — Admin → Strategy
 * ------------------------------------------------------------------
 * Visualizes escalation arcs across all active campaigns: behavioral
 * transitions, pacing evolution, emotional pressure progression,
 * replay trigger placement.
 */
import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { GitMerge, Flame } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  CAMPAIGN_CREATED_EVENT, CAMPAIGN_UPDATED_EVENT,
  fetchCampaigns, fetchCampaignSequences, ROLE_LABEL, type StrategicRole,
} from "@/lib/campaignEngine";

const ROLE_PRESSURE: Record<StrategicRole, number> = {
  hook_ambiguity: 25,
  behavioral_reveal: 50,
  escalation_confrontation: 95,
  pov_reframing: 60,
  replay_reinforcement: 40,
};
const PACING_LABEL: Record<string, string> = {
  "slow-burn": "slow-burn", measured: "measured", rapid: "rapid", reflective: "reflective", looping: "looping",
};

export default function NarrativeEscalationMapping() {
  const [campaigns, setCampaigns] = useState<any[]>([]);

  const load = async () => {
    const camps = await fetchCampaigns();
    const enriched = await Promise.all(camps.map(async (c: any) => ({ ...c, seq: await fetchCampaignSequences(c.id) })));
    setCampaigns(enriched.filter((c) => c.seq.length > 0));
  };

  useEffect(() => {
    load();
    const refresh = () => load();
    window.addEventListener(CAMPAIGN_CREATED_EVENT, refresh);
    window.addEventListener(CAMPAIGN_UPDATED_EVENT, refresh);
    const ch = (supabase as any).channel("narrative-map")
      .on("postgres_changes", { event: "*", schema: "public", table: "campaign_asset_sequences" }, refresh)
      .subscribe();
    return () => {
      window.removeEventListener(CAMPAIGN_CREATED_EVENT, refresh);
      window.removeEventListener(CAMPAIGN_UPDATED_EVENT, refresh);
      (supabase as any).removeChannel(ch);
    };
  }, []);

  return (
    <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <GitMerge className="w-5 h-5 text-cyan" />
          Narrative Escalation Mapping
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {campaigns.length === 0 && (
          <div className="text-xs text-muted-foreground italic">No narrative arcs to map yet.</div>
        )}
        {campaigns.map((c) => {
          const points = c.seq.map((s: any, idx: number) => ({
            x: idx,
            pressure: ROLE_PRESSURE[s.strategic_role as StrategicRole] ?? 30,
            role: s.strategic_role as StrategicRole,
            pacing: s.pacing_type as string,
            replay: Number(s.projected_replay_impact || 0),
            reinforcement: !!s.is_reinforcement,
            pivot: !!s.is_escalation_pivot,
          }));
          const w = 100, h = 70;
          const stepX = points.length > 1 ? w / (points.length - 1) : 0;
          const path = points.map((p: any, i: number) => `${i === 0 ? "M" : "L"} ${i * stepX} ${h - (p.pressure / 100) * h}`).join(" ");

          return (
            <div key={c.id} className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-xs text-foreground font-medium truncate">{c.campaign_name}</div>
                <Badge variant="outline" className="text-[10px] border-cyan/30 text-cyan">{c.dominant_archetype ?? "—"}</Badge>
              </div>

              <div className="relative rounded border border-glass-border bg-charcoal-deep/70 p-3">
                <svg viewBox={`0 0 ${w} ${h + 18}`} className="w-full h-32" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id={`grad-${c.id}`} x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--cyan))" stopOpacity="0.4" />
                      <stop offset="100%" stopColor="hsl(var(--cyan))" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  {/* baseline */}
                  <line x1="0" y1={h} x2={w} y2={h} stroke="currentColor" className="text-glass-border" strokeWidth="0.3" />
                  {/* area under curve */}
                  <path d={`${path} L ${(points.length - 1) * stepX} ${h} L 0 ${h} Z`} fill={`url(#grad-${c.id})`} />
                  {/* pressure curve */}
                  <path d={path} fill="none" stroke="hsl(var(--cyan))" strokeWidth="0.6" />
                  {/* nodes */}
                  {points.map((p: any, i: number) => (
                    <g key={i}>
                      <circle
                        cx={i * stepX}
                        cy={h - (p.pressure / 100) * h}
                        r={p.pivot ? 2.4 : p.reinforcement ? 2 : 1.4}
                        fill={p.pivot ? "#ef4444" : p.reinforcement ? "#34d399" : "hsl(var(--cyan))"}
                        stroke="hsl(var(--charcoal-deep))"
                        strokeWidth="0.4"
                      />
                      <text x={i * stepX} y={h + 6} textAnchor="middle" fontSize="3.4" fill="currentColor" className="text-muted-foreground">
                        #{i + 1}
                      </text>
                      <text x={i * stepX} y={h + 11} textAnchor="middle" fontSize="2.6" fill="currentColor" className="text-muted-foreground/70">
                        {PACING_LABEL[p.pacing] ?? p.pacing}
                      </text>
                    </g>
                  ))}
                </svg>

                <div className="flex flex-wrap gap-1.5 mt-2">
                  {points.map((p: any, i: number) => (
                    <span key={i} className="text-[9px] font-mono px-1.5 py-0.5 rounded border border-glass-border text-muted-foreground">
                      <span className="text-cyan">#{i + 1}</span> {ROLE_LABEL[p.role]}
                      {p.pivot && <span className="ml-1 text-red-400 inline-flex items-center gap-0.5"><Flame className="w-2 h-2" />pivot</span>}
                      {p.reinforcement && <span className="ml-1 text-emerald-400">⟳ replay</span>}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          );
        })}

        <Legend />
      </CardContent>
    </Card>
  );
}

function Legend() {
  return (
    <div className="flex flex-wrap gap-3 text-[10px] text-muted-foreground border-t border-glass-border pt-2">
      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-cyan" /> behavioral node</span>
      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> escalation pivot</span>
      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400" /> replay trigger</span>
      <span className="ml-auto opacity-70">y-axis = emotional pressure</span>
    </div>
  );
}
